import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Paper, Alert, Button, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

/**
 * AuthErrorPage Component
 * 
 * Displays authentication error messages and provides option to retry.
 */
const AuthErrorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const errorParam = searchParams.get('error');

    const errorMessages: Record<string, string> = {
      'access_denied': 'You denied access to your Strava account. Authorization is required to use this application.',
      'invalid_state': 'Authentication failed due to invalid state. This may be a security issue. Please try again.',
      'strava_auth_failed': 'Failed to authenticate with Strava. Please ensure you have a valid Strava account and try again.',
      'internal_error': 'An internal error occurred during authentication. Our team has been notified. Please try again later.',
      'invalid_scope': 'The required permissions were not granted. Please accept all requested permissions to use this application.',
    };

    setErrorMessage(
      errorMessages[errorParam || ''] || 'An unknown error occurred during authentication. Please try again.'
    );
  }, [searchParams]);

  const handleRetry = () => {
    navigate('/login');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <ErrorOutline
            sx={{
              fontSize: 64,
              color: 'error.main',
              mb: 2,
            }}
          />

          <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
            Authentication Failed
          </Typography>

          <Alert severity="error" sx={{ my: 3, textAlign: 'left' }}>
            {errorMessage}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If this problem persists, please contact support or try using a different browser.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleRetry}
              sx={{ px: 4 }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={handleGoHome}
              sx={{ px: 4 }}
            >
              Go Home
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AuthErrorPage;
